import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  makeStyles,
} from "@material-ui/core";
import RefreshIcon from "@material-ui/icons/Refresh";
import userFlowService from "../../../services/user-flow.service";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(3),
    gap: theme.spacing(2),
  },
  statCard: {
    height: "100%",
  },
  statLabel: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  section: {
    marginTop: theme.spacing(3),
  },
  table: {
    minWidth: 520,
  },
  empty: {
    padding: theme.spacing(4),
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
  error: {
    padding: theme.spacing(3),
    border: `1px solid ${theme.palette.error.light}`,
    color: theme.palette.error.main,
    background: `${theme.palette.error.light}22`,
    marginBottom: theme.spacing(2),
  },
  loading: {
    minHeight: 360,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  pathText: {
    direction: "ltr",
    textAlign: "left",
    fontFamily: "monospace",
    whiteSpace: "normal",
  },
}));

const emptySummary = {
  stats: {
    totalPageViews: 0,
    totalSessions: 0,
    averageDuration: 0,
    totalButtonClicks: 0,
  },
  pages: [],
  clicks: [],
  paths: [],
};

function formatDuration(ms) {
  if (!ms) return "۰ ثانیه";
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds} ثانیه`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes} دقیقه و ${seconds % 60} ثانیه`;
}

function StatCard({ label, value }) {
  const classes = useStyles();
  return (
    <Card className={classes.statCard}>
      <CardContent>
        <Typography className={classes.statLabel} variant="body2">
          {label}
        </Typography>
        <Typography variant="h5">{value}</Typography>
      </CardContent>
    </Card>
  );
}

function SimpleTable({ title, columns, rows, emptyText }) {
  const classes = useStyles();

  return (
    <Box className={classes.section}>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      <TableContainer component={Paper}>
        {!rows.length ? (
          <div className={classes.empty}>{emptyText}</div>
        ) : (
          <Table className={classes.table} size="small">
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell key={column.field}>{column.title}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => (
                <TableRow
                  key={row.id || row.pageUrl || row.eventName || row.path || index}
                >
                  {columns.map((column) => (
                    <TableCell key={column.field}>
                      {column.render
                        ? column.render(row[column.field], row)
                        : row[column.field]}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </TableContainer>
    </Box>
  );
}

export default function UserFlowAnalyticsPage() {
  const classes = useStyles();
  const [summary, setSummary] = useState(emptySummary);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const stats = summary.stats || emptySummary.stats;

  const statCards = useMemo(
    () => [
      { label: "تعداد کل بازدید صفحه", value: stats.totalPageViews || 0 },
      { label: "تعداد کل نشست‌ها", value: stats.totalSessions || 0 },
      {
        label: "میانگین زمان حضور",
        value: formatDuration(stats.averageDuration),
      },
      { label: "تعداد کل کلیک دکمه", value: stats.totalButtonClicks || 0 },
    ],
    [stats]
  );

  const fetchSummary = async () => {
    setLoading(true);
    setError(null);
    const res = await userFlowService.getSummary();
    if (res.error) {
      setError("امکان دریافت گزارش رفتار کاربران وجود ندارد.");
      setLoading(false);
      return;
    }
    setSummary(res.data || emptySummary);
    setLoading(false);
  };

  useEffect(() => {
    fetchSummary();
  }, []);

  if (loading) {
    return (
      <div className={classes.loading}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Box>
          <Typography variant="h5">User Flow Analytics</Typography>
          <Typography variant="body2" color="textSecondary">
            گزارش بازدید صفحات، نشست‌ها، کلیک دکمه‌ها و مسیرهای پرتکرار کاربران
          </Typography>
        </Box>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<RefreshIcon />}
          onClick={fetchSummary}
        >
          به‌روزرسانی
        </Button>
      </div>

      {error ? (
        <Paper className={classes.error}>
          <Typography>{error}</Typography>
        </Paper>
      ) : null}

      <Grid container spacing={2}>
        {statCards.map((item) => (
          <Grid item xs={12} sm={6} md={3} key={item.label}>
            <StatCard label={item.label} value={item.value} />
          </Grid>
        ))}
      </Grid>

      <SimpleTable
        title="صفحات"
        emptyText="هنوز داده‌ای برای بازدید صفحات ثبت نشده است."
        rows={summary.pages || []}
        columns={[
          { field: "pageUrl", title: "آدرس صفحه" },
          { field: "views", title: "تعداد بازدید" },
          {
            field: "averageDuration",
            title: "میانگین زمان حضور",
            render: (value) => formatDuration(value),
          },
        ]}
      />

      <SimpleTable
        title="کلیک دکمه‌ها"
        emptyText="هنوز کلیکی برای دکمه‌ها ثبت نشده است."
        rows={summary.clicks || []}
        columns={[
          { field: "eventName", title: "نام رویداد" },
          { field: "clickCount", title: "تعداد کلیک" },
        ]}
      />

      <SimpleTable
        title="مسیرهای پرتکرار"
        emptyText="هنوز مسیر پرتکراری ثبت نشده است."
        rows={summary.paths || []}
        columns={[
          {
            field: "path",
            title: "مسیر",
            render: (value) => <span className={classes.pathText}>{value}</span>,
          },
          { field: "count", title: "تعداد تکرار" },
        ]}
      />
    </div>
  );
}
